import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  PlayCircle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Filter,
  ChevronRight,
  Zap,
  Mail,
  Package,
  Rocket,
} from 'lucide-react';
import { format } from 'date-fns';
import { clsx } from 'clsx';

const API_BASE = '/api';

// Types
interface ProcessingRun {
  id: string;
  emailId?: string;
  classificationId?: string;
  orderId?: string;
  runType: 'email_intake' | 'order_parse' | 'deployment';
  runStatus: 'pending' | 'running' | 'completed' | 'failed';
  triggeredBy?: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  parserUsed?: string;
  itemsProcessed: number;
  itemsSuccessful: number;
  itemsFailed: number;
  errorMessage?: string;
  retryCount: number;
  createdAt: string;
}

interface RunStats {
  total: number;
  completed: number;
  failed: number;
  running: number;
  avgDurationMs: number;
  byType: Record<string, number>;
  byParser: Record<string, number>;
}

// Status configuration
const statusConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string; label: string }> = {
  pending: { icon: Clock, color: 'text-gray-600', bgColor: 'bg-gray-100', label: 'Pending' },
  running: { icon: RefreshCw, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Running' },
  completed: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Completed' },
  failed: { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100', label: 'Failed' },
};

const runTypeConfig: Record<string, { icon: React.ElementType; label: string; color: string }> = {
  email_intake: { icon: Mail, label: 'Email Intake', color: 'text-blue-600' },
  order_parse: { icon: Package, label: 'Order Parse', color: 'text-purple-600' },
  deployment: { icon: Rocket, label: 'Deployment', color: 'text-orange-600' },
};

function formatDuration(ms?: number): string {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

export default function ProcessingHistoryPage() {
  const [filters, setFilters] = useState({
    runType: '',
    runStatus: '',
    page: 1,
    pageSize: 20,
  });

  // Fetch runs
  const { data: runsData, isLoading, refetch } = useQuery<{
    data: ProcessingRun[];
    pagination: { totalItems: number; totalPages: number };
  }>({
    queryKey: ['processing-runs', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', filters.page.toString());
      params.set('pageSize', filters.pageSize.toString());
      if (filters.runType) params.set('runType', filters.runType);
      if (filters.runStatus) params.set('runStatus', filters.runStatus);

      const res = await fetch(`${API_BASE}/processing/runs?${params}`);
      if (!res.ok) throw new Error('Failed to fetch runs');
      return res.json();
    },
    refetchInterval: 5000, // Auto-refresh every 5s
  });

  // Fetch stats
  const { data: statsData } = useQuery<{ data: RunStats }>({
    queryKey: ['processing-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/processing/runs/stats`);
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    },
    refetchInterval: 10000,
  });

  const runs = runsData?.data || [];
  const stats = statsData?.data;
  const pagination = runsData?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Processing History</h1>
          <p className="text-gray-500 mt-1">Track order processing like n8n workflow executions</p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Zap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Runs (24h)</p>
                <p className="text-xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-xl font-bold text-gray-900">{stats.completed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Failed</p>
                <p className="text-xl font-bold text-gray-900">{stats.failed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Avg Duration</p>
                <p className="text-xl font-bold text-gray-900">{formatDuration(stats.avgDurationMs)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          <select
            value={filters.runType}
            onChange={(e) => setFilters({ ...filters, runType: e.target.value, page: 1 })}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="">All Types</option>
            <option value="email_intake">Email Intake</option>
            <option value="order_parse">Order Parse</option>
            <option value="deployment">Deployment</option>
          </select>

          <select
            value={filters.runStatus}
            onChange={(e) => setFilters({ ...filters, runStatus: e.target.value, page: 1 })}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>

          {(filters.runType || filters.runStatus) && (
            <button
              onClick={() => setFilters({ ...filters, runType: '', runStatus: '', page: 1 })}
              className="text-sm text-perfectted-primary hover:text-perfectted-dark"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Runs List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-perfectted-primary" />
          </div>
        ) : runs.length === 0 ? (
          <div className="text-center py-12">
            <PlayCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No processing runs</h3>
            <p className="text-gray-500 mt-1">Runs will appear here when emails are processed</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parser</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {runs.map((run) => {
                    const status = statusConfig[run.runStatus];
                    const StatusIcon = status.icon;
                    const typeConfig = runTypeConfig[run.runType];
                    const TypeIcon = typeConfig.icon;

                    return (
                      <tr key={run.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className={clsx('flex items-center gap-2 px-2 py-1 rounded-full w-fit', status.bgColor)}>
                            <StatusIcon className={clsx('w-4 h-4', status.color, run.runStatus === 'running' && 'animate-spin')} />
                            <span className={clsx('text-sm font-medium', status.color)}>{status.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <TypeIcon className={clsx('w-4 h-4', typeConfig.color)} />
                            <span className="text-sm text-gray-900">{typeConfig.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">{run.parserUsed || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <span className="text-green-600">{run.itemsSuccessful}</span>
                            {run.itemsFailed > 0 && (
                              <span className="text-red-600 ml-1">/ {run.itemsFailed} failed</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">{formatDuration(run.durationMs)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">
                            {run.startedAt ? format(new Date(run.startedAt), 'dd MMM HH:mm:ss') : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/processing/${run.id}`}
                            className="flex items-center gap-1 text-sm text-perfectted-primary hover:text-perfectted-dark"
                          >
                            View Steps
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {runs.map((run) => {
                const status = statusConfig[run.runStatus];
                const StatusIcon = status.icon;
                const typeConfig = runTypeConfig[run.runType];
                const TypeIcon = typeConfig.icon;

                return (
                  <Link key={run.id} to={`/processing/${run.id}`} className="block p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className={clsx('p-1.5 rounded-lg', status.bgColor)}>
                          <StatusIcon className={clsx('w-4 h-4', status.color, run.runStatus === 'running' && 'animate-spin')} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <TypeIcon className={clsx('w-4 h-4', typeConfig.color)} />
                            <span className="font-medium text-gray-900">{typeConfig.label}</span>
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {run.parserUsed || 'No parser'} &bull; {formatDuration(run.durationMs)}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                      <span>{run.startedAt ? format(new Date(run.startedAt), 'dd MMM HH:mm') : '-'}</span>
                      <span className="text-green-600">{run.itemsSuccessful} success</span>
                      {run.itemsFailed > 0 && <span className="text-red-600">{run.itemsFailed} failed</span>}
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Page {filters.page} of {pagination.totalPages} ({pagination.totalItems} total)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                    disabled={filters.page === 1}
                    className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                    disabled={filters.page >= pagination.totalPages}
                    className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
